/**
 * Every fact about the legal entity, in one place.
 *
 * Razorpay's merchant onboarding checks the website for a Privacy Policy,
 * Pricing, About, Contact and a Refund/Cancellation policy, all live and
 * accurate, before it issues live keys. India's DPDP Act separately expects a
 * named grievance officer with a working contact. Both of those want the same
 * handful of facts repeated across four pages, and four copies is four things
 * to forget when one changes.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * FILL THESE IN BEFORE THE SITE GOES LIVE. Every value marked TODO renders
 * VISIBLY on the page as a bracketed placeholder rather than silently as an
 * empty string, because a blank line in a privacy policy is a thing nobody
 * notices and a bracketed TODO is a thing everybody does.
 *
 * Every value below is now real, so the `TODO()` helper that produced those
 * markers is gone -- CI builds the site with warnings as errors and an unused
 * binding fails the build. `isPlaceholder` stays: it is the contract a
 * reinstated placeholder has to satisfy, and verify-launch-details is what
 * catches one that never got filled in.
 * ────────────────────────────────────────────────────────────────────────────
 */

import pricingSnapshot from "./pricing.snapshot.json";

export const COMPANY = {
  /** Product name. The apps, the listings and the emails all say this. */
  product: "Varzish Mate",

  /**
   * How the brand relates to the entity that is actually registered.
   *
   * Varzish Mate is not registered anywhere and does not need to be: Vira
   * Consultancy is a sole proprietorship, which in Indian law is not a separate
   * legal person, so the proprietor may trade under any number of brand names.
   *
   * This sentence is not decoration. Razorpay's website review, Meta's WhatsApp
   * display-name review and Play's developer verification all check the LEGAL
   * ENTITY, not the brand -- and a site branded one way against a KYC file
   * branded another, with nothing connecting them, is the most common reason an
   * Indian merchant application comes back.
   */
  entity: "Varzish Mate is a product of Vira Consultancy, a sole proprietorship registered in India.",

  /** Registered legal name. Must match the bank account and the GST records. */
  legalName: "Vira Consultancy",

  /** Registered address, as one string. Appears on invoices and in the policy. */
  address: "Shop No. 1, Captain House, Dholas Road, Narodara Rural, Sikar, Rajasthan, 332311",

  /**
   * GSTIN. Empty string means the row is not rendered -- which is why an
   * unfilled one is invisible rather than loud, and why verify-launch-details
   * now asserts it is present instead of only looking for a TODO marker.
   *
   * The leading "08" is the Rajasthan state code and must agree with the state
   * in `address` above; the PAN inside it (JWWPK2225N) carries "P" in its
   * fourth position, which is the proprietorship that makes Vira Consultancy
   * and its proprietor the same legal person. Razorpay compares all three.
   */
  gstin: "08JWWPK2225N1ZN",

  /**
   * Support address. Razorpay looks for a real, monitored one; a Gmail address
   * on a different brand is what was here before.
   */
  supportEmail: "support@varzishmate.com",

  /**
   * Required by the DPDP Act 2023. A named person, not "the team" -- the point
   * of the provision is that a complaint reaches somebody accountable.
   *
   * The address is the support mailbox on purpose, and referenced rather than
   * repeated. The Act wants a contact that is monitored, not a dedicated one,
   * and a second address that quietly stops being read is worse than one that
   * is. Referencing it also means a mailbox move changes one literal instead of
   * two that can drift apart.
   */
  grievanceOfficer: {
    name: "Ankit Khyalia",
    get email() {
      return COMPANY.supportEmail;
    },
  },

  /** Where each app lives, and where the console is served. */
  urls: {
    site: "https://varzishmate.com",
    console: "https://app.varzishmate.com",
    playClub: "https://play.google.com/store/apps/details?id=com.lfit.lfitclub",
    playMember: "https://play.google.com/store/apps/details?id=com.ajaykumarsaharan.lfit",
    appStoreClub: "https://apps.apple.com/in/app/varzish-mate/id6823854751",
    // Apple's provider token (App Store Connect, Analytics, Campaigns, the
    // `pt=` value in any campaign link). Public: it rides every App Store
    // link. While empty, App Store links go out untagged.
    appStoreProviderToken: "129629877",
    youtube: "https://www.youtube.com/@varzishMateClub",
    // The same channel by its permanent id, which survives a handle change.
    youtubeChannel: "https://www.youtube.com/channel/UCL1STgEZps5ReVXaM3MaEkw",
  },

  /**
   * The brand's profiles elsewhere on the web, for Organization.sameAs. Naming
   * them ties the profiles to this site for search and answer engines. Add a
   * profile here the day it is live (LinkedIn, Crunchbase, G2 ...); never a
   * placeholder.
   */
  get sameAs() {
    return [COMPANY.urls.youtube, COMPANY.urls.youtubeChannel];
  },

  /**
   * Kept in one place because three pages quote it. Read from the price
   * snapshot, so a trial length changed in the database reaches the site on
   * the next build rather than waiting for someone to edit this file.
   */
  trialDays: pricingSnapshot.trialDays,
};

/** True when a value is still a placeholder, so a build check can catch it. */
export const isPlaceholder = (value) =>
  typeof value === "string" && value.startsWith("[TODO:");

/** Every placeholder still on the site. Empty means ready to launch. */
export function outstandingPlaceholders(company = COMPANY) {
  const found = [];
  const walk = (node, path) => {
    for (const [key, value] of Object.entries(node)) {
      const here = path ? `${path}.${key}` : key;
      if (value && typeof value === "object") walk(value, here);
      else if (isPlaceholder(value)) found.push(here);
    }
  };
  walk(company, "");
  return found;
}
